import { useState, useMemo, useDeferredValue, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMapStore } from '@/store/mapStore';
import { PlaceSummary } from '@/hooks/usePlaces';
import { PlaceCategory } from '@/types/places';

const RECENT_SEARCHES_KEY = 'jewmap-recent-searches';
const MAX_RECENT_SEARCHES = 5;

// Mapping from French labels to category enum
const categoryMapping: Record<string, PlaceCategory> = {
  'synagogues': 'synagogue',
  'synagogue': 'synagogue',
  'restaurants': 'restaurant',
  'restaurant': 'restaurant',
  'épiceries': 'grocery',
  'épicerie': 'grocery',
  'grocery': 'grocery',
  'boulangeries': 'bakery',
  'boulangerie': 'bakery',
  'bakery': 'bakery',
  'boucheries': 'butcher',
  'boucherie': 'butcher',
  'butcher': 'butcher',
  'mikvaot': 'mikvah',
  'mikvah': 'mikvah',
  'mikvé': 'mikvah',
  'communautés': 'community',
  'communauté': 'community',
  'community': 'community',
};

const getStoredRecentSearches = (): PlaceSummary[] => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

interface AutocompleteSuggestion {
  id: string;
  name: string;
  category: PlaceCategory;
  city: string;
}

export const useSearchPlaces = () => {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const { userLocation, activeCategories, activeKosherStatuses, activeKosherAuthorities } = useMapStore();
  const [recentSearches, setRecentSearches] = useState<PlaceSummary[]>(getStoredRecentSearches);

  // Create stable filter key for proper cache invalidation
  const filtersKey = useMemo(() => JSON.stringify({
    categories: [...activeCategories].sort(),
    statuses: [...activeKosherStatuses].sort(),
    authorities: [...activeKosherAuthorities].sort()
  }), [activeCategories, activeKosherStatuses, activeKosherAuthorities]);

  // Fast autocomplete suggestions
  const { data: suggestions = [], isLoading: isSuggestionsLoading } = useQuery({
    queryKey: ['autocomplete-places', query],
    queryFn: async () => {
      if (!query.trim() || query.trim().length < 2) return [];
      
      const { data, error } = await supabase.rpc('autocomplete_places', {
        search_query: query,
        limit_count: 8,
      });

      if (error) throw error;
      return (data as AutocompleteSuggestion[]) || [];
    },
    enabled: query.trim().length >= 2,
    staleTime: 1000 * 60,
  });

  // Smart suggestions algorithm - combines nearby + scoring
  const { data: smartSuggestions = [], isLoading: isSuggestionsLoadingNearby } = useQuery({
    queryKey: ['smart-suggestions', userLocation, recentSearches.map(r => r.category)],
    queryFn: async () => {
      // Fetch places to suggest
      const { data, error } = await supabase.rpc('search_places_v2', {
        search_query: null,
        category_filters: null,
        city_filter: null,
        user_lat: userLocation ? userLocation[1] : null,
        user_lng: userLocation ? userLocation[0] : null,
        radius_km: userLocation ? 25 : 500,
        limit_count: 50,
        kosher_statuses: null,
        kosher_authorities: null,
      });

      if (error) throw error;
      
      const places = (data as PlaceSummary[]) || [];
      
      // Calculate scores for each place
      const scoredPlaces = places.map(place => {
        let score = 0;
        
        // 1. Distance score (closer = higher score, max 40 points)
        if (place.distance_km !== null && place.distance_km !== undefined) {
          if (place.distance_km < 1) score += 40;
          else if (place.distance_km < 3) score += 35;
          else if (place.distance_km < 5) score += 30;
          else if (place.distance_km < 10) score += 20;
          else if (place.distance_km < 15) score += 10;
          else score += 5;
        }
        
        // 2. Rating score (max 25 points)
        if (place.rating) {
          score += Math.min(place.rating * 5, 25);
        }
        
        // 3. Review count score (popularity, max 15 points)
        if (place.review_count) {
          if (place.review_count >= 50) score += 15;
          else if (place.review_count >= 20) score += 12;
          else if (place.review_count >= 10) score += 8;
          else if (place.review_count >= 5) score += 5;
          else score += 2;
        }
        
        // 4. Featured places bonus (max 30 points)
        if (place.is_featured) score += 30;
        
        // 5. Sponsorship tier bonus (max 20 points)
        if (place.sponsorship_tier === 'featured') score += 20;
        else if (place.sponsorship_tier === 'premium') score += 15;
        else if (place.sponsorship_tier === 'basic') score += 10;
        
        // 6. Category affinity based on recent searches (max 10 points)
        const recentCategories = recentSearches.map(r => r.category);
        if (recentCategories.includes(place.category)) {
          score += 10;
        }
        
        // 7. Random factor for variety (0-5 points)
        score += Math.random() * 5;
        
        return { ...place, _score: score };
      });
      
      // Sort by score descending and take top 15
      scoredPlaces.sort((a, b) => b._score - a._score);
      return scoredPlaces.slice(0, 15);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes cache
  });

  // Full search results with multi-select filters
  const { data: results = [], isLoading: isQueryLoading } = useQuery({
    queryKey: ['search-places', deferredQuery, userLocation, filtersKey],
    queryFn: async () => {
      const hasFilters = activeKosherStatuses.length > 0 || activeKosherAuthorities.length > 0 || activeCategories.length > 0;
      
      if (!deferredQuery.trim() && !hasFilters) {
        return [];
      }
      
      // Check if query matches a category
      const normalizedQuery = deferredQuery.trim().toLowerCase();
      const categoryMatch = categoryMapping[normalizedQuery];
      
      // Merge categories: query match + active filters
      let categoryFilters: PlaceCategory[] | null = null;
      if (categoryMatch) {
        categoryFilters = [categoryMatch];
      } else if (activeCategories.length > 0) {
        categoryFilters = activeCategories;
      }
      
      console.log('[useSearchPlaces] Fetching with:', {
        query: deferredQuery,
        categories: categoryFilters,
        statuses: activeKosherStatuses,
        authorities: activeKosherAuthorities
      });
      
      const { data, error } = await supabase.rpc('search_places_v2', {
        search_query: categoryMatch ? null : (deferredQuery.trim() || null),
        category_filters: categoryFilters,
        city_filter: null,
        user_lat: userLocation ? userLocation[1] : null,
        user_lng: userLocation ? userLocation[0] : null,
        radius_km: 500,
        limit_count: 30,
        kosher_statuses: activeKosherStatuses.length > 0 ? activeKosherStatuses : null,
        kosher_authorities: activeKosherAuthorities.length > 0 ? activeKosherAuthorities : null,
      });

      if (error) throw error;
      return (data as PlaceSummary[]) || [];
    },
    enabled: deferredQuery.trim().length > 0 || activeKosherStatuses.length > 0 || activeKosherAuthorities.length > 0 || activeCategories.length > 0,
  });

  const isLoading = query !== deferredQuery || isQueryLoading;

  const groupedResults = useMemo(() => {
    const groups: Record<PlaceCategory, PlaceSummary[]> = {} as Record<PlaceCategory, PlaceSummary[]>;
    results.forEach((place) => {
      if (!groups[place.category]) {
        groups[place.category] = [];
      }
      groups[place.category].push(place);
    });
    return groups;
  }, [results]);

  const addToRecentSearches = useCallback((place: PlaceSummary) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((p) => p.id !== place.id);
      const updated = [place, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  }, []);

  return {
    query,
    setQuery,
    results,
    groupedResults,
    isLoading,
    hasResults: results.length > 0,
    recentSearches,
    addToRecentSearches,
    clearRecentSearches,
    suggestions,
    isSuggestionsLoading,
    smartSuggestions,
    isSuggestionsLoadingNearby,
  };
};
